import Immutable from 'seamless-immutable';

import { forgotPassword, authentication } from '~/api';
import { reportPreAuthError } from '~/common/helpers/diagnostics/reportPreAuthError';
import i18n from '~/common/helpers/i18n';
import { executeRecaptcha } from '~/common/helpers/recaptcha/executeRecaptcha';

const initialState = Immutable({
  isSendEmail: false,
  isInvalidLink: false,
  disabledForgotPassword: false,
  loading: false,
  error: {
    sendEmail: null,
    resetPassword: {
      unauthorized: null,
      password: null
    }
  },
  success: {
    isResetpassword: false,
    resetPassword: null
  },
  resetInfo: {
    realm: null,
    email: null
  },
  resetInfoLoading: true
});

const getValueInProps = details => {
  if (details) {
    const keys = Object.keys(details);
    return keys.reduce((accumulator, currentValue) => {
      const messages = details[currentValue].map(x => x.message);
      return {
        message: messages.toString()
      };
    }, '');
  }
  return null;
};

const normalizeErros = error => {
  const errorData = error.details || error.data;
  if (!errorData) {
    return {
      unauthorized: i18n.t('message.error.reset_password_error'),
      password: null
    };
  }
  return {
    unauthorized: null,
    password: getValueInProps(errorData?.fieldErrors)
      ? getValueInProps(errorData?.fieldErrors).message
      : '',
    messages: errorData?.fieldErrors
  };
};

const forgotPasswordModel = {
  name: 'forgotPassword',
  state: initialState,
  reducers: {
    sendEmail: (state, isSendEmail) => state.merge({ isSendEmail }),
    setInvalidLink: (state, value) => state.setIn(['isInvalidLink'], value),
    setResetInfo: (state, value) => state.merge({ resetInfo: value }),
    setResetInfoLoading: (state, value) =>
      state.setIn(['resetInfoLoading'], value),
    setDisableForgotPassword: (state, value) =>
      state.setIn(['disabledForgotPassword'], value),
    loading: (state, loading) => state.merge({ loading }),
    success: (state, { path, value }) =>
      state.merge(
        {
          success: {
            [path]: value
          }
        },
        { deep: true }
      ),
    error: (state, { path, value }) =>
      state.merge(
        {
          error: {
            [path]: value
          }
        },
        { deep: true }
      )
  },
  effects: dispatch => ({
    async send(params) {
      try {
        const recaptcha = await executeRecaptcha({
          action: 'reset-password',
          realm: params?.realm
        });
        const { data } = await authentication.getNotLoggedPlatformParameter(
          params?.realm,
          'disabledForgotPassword'
        );
        if (data?.disabledForgotPassword) {
          dispatch.forgotPassword.setDisableForgotPassword(
            data?.disabledForgotPassword
          );
          return;
        }
        await forgotPassword.send({ ...params, recaptcha });
        dispatch.forgotPassword.sendEmail(true);
      } catch (e) {
        reportPreAuthError(e, {
          stage: 'request',
          action: 'reset-password',
          realm: params?.realm
        });
        dispatch.forgotPassword.error({
          path: 'sendEmail',
          value: e
        });
      }
    },
    returnToSignIn() {
      dispatch.router.navigate({ to: '/login' });
    },
    returnToForgotPassword() {
      dispatch.router.navigate({ to: '/login/forgotpassword' });
    },
    async fetchResetInfo(params) {
      dispatch.forgotPassword.setResetInfo({ realm: null, email: null });
      dispatch.forgotPassword.setResetInfoLoading(true);
      try {
        const { data } = await forgotPassword.resetInfo({
          changePasswordId: params?.changePasswordId
        });
        dispatch.forgotPassword.setResetInfo({
          realm: data?.realm ?? null,
          email: data?.email ?? null
        });
      } catch {
        dispatch.forgotPassword.setResetInfo({ realm: null, email: null });
      } finally {
        dispatch.forgotPassword.setResetInfoLoading(false);
      }
    },
    async onResetPassword(params) {
      dispatch.forgotPassword.loading(true);
      try {
        await forgotPassword.resetPassword(params);
        dispatch.forgotPassword.success({
          path: 'resetPassword',
          value: true
        });
      } catch (error) {
        // RESET_LINK_SUPERSEDED (link substituído por um mais recente) e
        // RESET_LINK_EXPIRED (link fora do prazo) vêm do auth-management com
        // status 401/400. Ambos caem no mesmo estado de link inválido (DST-4550).
        // O status 404 cobre o POST change-password quando o id já não existe.
        const errorCode = error?.response?.data?.data?.code;
        if (
          errorCode === 'RESET_LINK_SUPERSEDED' ||
          errorCode === 'RESET_LINK_EXPIRED' ||
          error?.response?.status === 404
        ) {
          dispatch.forgotPassword.setInvalidLink(true);
        }
        dispatch.forgotPassword.error({
          value: normalizeErros(error.response ? error.response.data : error),
          path: 'resetPassword'
        });
      } finally {
        dispatch.forgotPassword.loading(false);
      }
    }
  }),
  logics: [
    {
      type: 'forgotPassword/returnToForgotPassword',
      latest: true,
      process(context, dispatch, done) {
        dispatch.forgotPassword.setInvalidLink(false);
        done();
      }
    },
    {
      type: 'forgotPassword/returnToSignIn',
      latest: true,
      process(context, dispatch, done) {
        dispatch.forgotPassword.sendEmail(false);
        done();
      }
    },
    {
      type: 'forgotPassword/send',
      latest: true,
      process(context, dispatch, done) {
        dispatch.forgotPassword.loading(true);
        done();
      }
    },
    {
      type: [
        'forgotPassword/error',
        'forgotPassword/sendEmail',
        'forgotPassword/setDisableForgotPassword'
      ],
      latest: true,
      process(_, dispatch, done) {
        dispatch.forgotPassword.loading(false);
        done();
      }
    }
  ]
};

export default forgotPasswordModel;
